import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import ProjectsData from '@/ts/ProjectsData';
import { ProjectDataResponse } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';

@Module({name: 'Projects', namespaced: true})
export default class Projects extends VuexModule {
    public projects: ProjectsData[] = [];
    public projectsMap: { [index: number]: number } = {};
    public projectId: number;
    public cachedProject: ProjectsData = null;
    public activeProjectId: number = null;
    public editFormVisible: boolean = false;
    public projectToEdit: ProjectsData = null;
    public selectedProject: any = null;
    public projectConfigs: any = null;

    @Mutation
    public RESET_PROJECTS_STORE_STATE(): void {
        this.projects        = [];
        this.projectsMap     = {};
        this.projectId       = null;
        this.cachedProject   = null;
        this.activeProjectId = null;
        this.editFormVisible = false;
        this.projectToEdit   = null;
        this.selectedProject = null;
        this.projectConfigs  = null;
    }

    get allProjects() {
        return this.projects.filter((project: ProjectsData) => project.persisted);
    }

    get getProjectConfigs() {
        return this.projectConfigs;
    }

    get projectIds() {
        return this.projects.map((project: ProjectsData) => {
            return project.id;
        });
    }

    get getProject() {
        return ((projectId: number) => {
            return this.projects[this.projectsMap[projectId]];
        });
    }

    get getSelectedProject() {
        return this.selectedProject;
    }

    get selectedProjects() {
        return this.projects.filter((item) => {
            return item.selected === true;
        });
    }

    get selectedProjectIds() {
        return store.getters['Projects/selectedProjects']
            .map((project: ProjectsData) => project.id);
    }

    get editMenuVisible(): boolean {
        return this.editFormVisible;
    }

    get activeProject() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]];
    }

    get getProjectId() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]].id;
    }

    get getProjectName() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]].name;
    }

    get getProjectOwner() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]].owner;
    }

    get getProjectCorporationId() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]].corporationId;
    }

    get getProjectDescription() {
        if (this.activeProjectId === null) {
            return null;
        }

        return this.projects[this.projectsMap[this.activeProjectId]].description;
    }

    get getProjectToEdit() {
        return this.projectToEdit;
    }

    @Action
    public FETCH_PROJECT(projectId: number) {
        axios.get(Endpoints.Admin.Projects.SINGLE_PROJECT(projectId))
            .then((response) => {
                store.commit(Mutations.SET_SELECTED_PROJECT, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public INITIATE_PROJECT() {
        store.commit(Mutations.INITIATE_NEW_PROJECT);
    }

    @Action
    public FETCH_PROJECTS() {
        return axios.get(Endpoints.Admin.Projects.PROJECTS)
            .then((response) => {
                store.commit(Mutations.ADD_PROJECTS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public FETCH_PROJECT_CONFIGS() {
        axios.get(Endpoints.Admin.Projects.PROJECT_CONFIGS)
            .then((response) => {
                store.commit(Mutations.SET_PROJECT_CONFIGS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public POST_PROJECTS(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('name', data.name);
        requestPayload.append('description', data.description);
        requestPayload.append('image', data.image);

        axios.post(Endpoints.Admin.Projects.PROJECTS, requestPayload, headers)
            .then((response) => {
                store.commit(Mutations.ADD_PROJECT, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_PROJECT_CONFIGS(config: string) {
        axios.post(Endpoints.Admin.Projects.PROJECT_CONFIGS, {config})
            .then((response) => {
                store.commit(Mutations.SET_PROJECT_CONFIGS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_PROJECT_CONTACT(data: any) {
        axios.patch(Endpoints.Admin.Projects.PROJECT_CONTACT(data.projectId), data)
            .then((response) => {
                store.commit(Mutations.UPDATE_PROJECT_COMPANY_CONTACT, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_PROJECT(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
                'Accept': 'application/json',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('_method', 'PATCH');

        requestPayload.append('name', data.name);
        requestPayload.append('description', data.description);
        requestPayload.append('primaryColor', data.primaryColor);
        requestPayload.append('secondaryColor', data.secondaryColor);
        requestPayload.append('primaryLogo', data.mainLogo);
        requestPayload.append('squireLogo', data.squireLogo);
        requestPayload.append('image', data.image);
        requestPayload.append('initialSquireLogo', data.initialSquireLogo);
        requestPayload.append('initialPrimaryLogo', data.initialMainLogo);
        requestPayload.append('initialImage', data.initialImage);

        axios.post(Endpoints.Admin.Projects.SINGLE_PROJECT(data.id), requestPayload, headers)
            .then((response) => {
                store.commit(Mutations.UPDATE_CACHED_PROJECT, response.data.data);
                store.commit(Mutations.UPDATE_SELECTED_PROJECT, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public ADD_PROJECT(project: ProjectDataResponse): void {
        let arrayIndex = null;

        if (!this.projectsMap.hasOwnProperty(project.id)) {
            arrayIndex = this.projects.push(new ProjectsData(
                project.id,
                project.corporationId,
                project.name,
                project.owner,
                project.description,
                project.totalBuildings,
                project.totalProperties,
                project.totalVacantProperties,
                project.totalResidents,
                project.image,
            )) - 1;
            this.projectsMap[project.id] = arrayIndex;
        }
    }

    @Mutation
    public ADD_PROJECTS(projectData: ProjectDataResponse[]): void {
        if (!projectData.length) {
            this.projects    = [];
            this.projectsMap = {};
        }

        for (const project of projectData) {
            let arrayIndex = null;

            if (!this.projectsMap.hasOwnProperty(project.id)) {
                arrayIndex = this.projects.push(new ProjectsData(
                    project.id,
                    project.corporationId,
                    project.name,
                    project.owner,
                    project.description,
                    project.totalBuildings,
                    project.totalProperties,
                    project.totalVacantProperties,
                    project.totalResidents,
                    project.image,
                )) - 1;
                this.projectsMap[project.id] = arrayIndex;
            } else {
                const existingProject = this.projects[this.projectsMap[project.id]];

                if (existingProject) {
                    existingProject.updateLocalProjectData(project);
                }
            }
        }
    }

    @Mutation
    public UPDATE_CACHED_PROJECT(projectData: ProjectDataResponse): void {
        this.projects[this.projectsMap[projectData.id]].id = projectData.id;
        this.projects[this.projectsMap[projectData.id]].corporationId = projectData.corporationId;
        this.projects[this.projectsMap[projectData.id]].name = projectData.name;
        this.projects[this.projectsMap[projectData.id]].owner = projectData.owner;
        this.projects[this.projectsMap[projectData.id]].description = projectData.description;
        this.projects[this.projectsMap[projectData.id]].totalBuildings = projectData.totalBuildings;
        this.projects[this.projectsMap[projectData.id]].totalProperties = projectData.totalProperties;
        this.projects[this.projectsMap[projectData.id]].totalVacantProperties = projectData.totalVacantProperties;
        this.projects[this.projectsMap[projectData.id]].totalResidents = projectData.totalResidents;
        this.projects[this.projectsMap[projectData.id]].image = projectData.image;
    }

    @Mutation
    public UPDATE_SELECTED_PROJECT(projectData: any): void {
       this.selectedProject = projectData;
    }

    @Mutation
    public UPDATE_PROJECT_NAME(value: string): void {
        this.projects[this.projectsMap[this.activeProjectId]].name = value;
    }

    @Mutation
    public UPDATE_PROJECT_OWNER(value: number): void {
        this.projects[this.projectsMap[this.activeProjectId]].owner = value;
    }

    @Mutation
    public UPDATE_PROJECT_DESCRIPTION(value: string): void {
        this.projects[this.projectsMap[this.activeProjectId]].description = value;
    }

    @Mutation
    public UPDATE_ACTIVE_PROJECT(projectId: number): void {
        this.activeProjectId = projectId;
    }

    @Mutation
    public EDIT_ACTIVE_PROJECT(): void {
        this.cachedProject = this.projects[this.projectsMap[this.activeProjectId]];
    }

    @Mutation
    public INITIATE_NEW_PROJECT() {
        const corporationId = store.state.Corporations.corporationId;
        const activeIndex = this.projects.push(new ProjectsData(-1, corporationId)) - 1;
        this.projectsMap[-1] = activeIndex;
        this.activeProjectId = -1;
    }

    @Mutation
    public SHOW_EDIT_PROJECT_FORM() {
        this.editFormVisible = true;
    }

    @Mutation
    public HIDE_EDIT_PROJECT_FORM() {
        this.editFormVisible = false;
    }

    @Mutation
    public SELECT_PROJECT(itemId: number) {
        for (const project of this.projects) {
            if (project.id !== itemId) {
                this.projects[this.projectsMap[project.id]].selected = false;
            }
        }

        this.projects[this.projectsMap[itemId]].selected = true;
        this.activeProjectId = itemId;
    }

    @Mutation
    public SET_SELECTED_PROJECT(project: any) {
        this.selectedProject = project;
    }

    @Mutation
    public SET_PROJECT_TO_EDIT(project: ProjectsData|null) {
        this.projectToEdit = project;
    }

    @Mutation
    public DESELECT_PROJECT(itemId: number) {
        if (this.activeProjectId && this.projects[this.projectsMap[itemId]].selected === true) {
            this.projects[this.projectsMap[itemId]].selected = false;
            this.activeProjectId = null;
        }

        this.projects[this.projectsMap[itemId]].selected = false;
    }

    @Mutation
    public UPDATE_PERSISTED_PROJECT(newProjectId: number) {
        const currentArrayIndex = this.projectsMap[this.activeProjectId];
        this.activeProjectId = null;
        this.projectsMap[newProjectId] = currentArrayIndex;
        this.projects[currentArrayIndex].id = newProjectId;
        delete this.projectsMap[-1];
    }

    @Mutation
    public UPDATE_PROJECT_COMPANY_CONTACT(companyContact: any) {
        this.selectedProject.companyContact = companyContact;
    }

    @Mutation
    public SET_PROJECT_CONFIGS(data: any) {
        this.projectConfigs = data ? data.config.view : 'grid';
    }
}

